import React, { useState, useRef, useEffect } from 'react';
import { X, Volume2, VolumeX, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const videos = [
  {
    id: 1,
    url: "https://customer-assets.emergentagent.com/job_snip-style-27/artifacts/8zneg43y_snaptik_7588314179455438101_v2.mp4",
    title: "Los Angeles",
    startTime: 0,
    description: {
      fr: "Référence GTA V - L'ambiance californienne avec style et attitude.",
      en: "GTA V Reference - California vibes with style and attitude."
    }
  },
  {
    id: 2,
    url: "https://customer-assets.emergentagent.com/job_snip-style-27/artifacts/n1a0eatm_snaptik_7504361698913029382_v2.mp4",
    title: "Favela, Rio De Janeiro",
    startTime: 0,
    description: {
      fr: "L'énergie du Brésil, des styles audacieux inspirés des rues de Rio.",
      en: "Brazilian energy, bold styles inspired by the streets of Rio."
    }
  },
  {
    id: 3,
    url: "https://customer-assets.emergentagent.com/job_snip-style-27/artifacts/7bdcofl7_snaptik_7522624482771504440_v2.mp4",
    title: "Miami",
    startTime: 0,
    description: {
      fr: "Le luxe de Miami, des coupes premium avec style et attitude.",
      en: "Miami luxury, premium cuts with style and attitude."
    }
  }
];

export const VideoGallery = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const modalVideoRef = useRef(null);
  const sliderRef = useRef(null);
  const { language } = useLanguage();

  const openModal = (video) => {
    setSelectedVideo(video);
    setIsMuted(false);
  };

  const closeModal = () => {
    setSelectedVideo(null);
    setIsMuted(true);
  };

  const toggleMute = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.muted = !modalVideoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % videos.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Auto-slide every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 6000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const getVideoStyle = (index) => {
    const diff = index - currentIndex;
    const normalizedDiff = ((diff + videos.length) % videos.length);
    
    if (normalizedDiff === 0) {
      // Center (active)
      return {
        transform: 'translateX(0) scale(1)',
        opacity: 1,
        zIndex: 30,
        filter: 'brightness(1)'
      };
    } else if (normalizedDiff === 1 || (normalizedDiff === videos.length - 1 && videos.length > 2)) {
      // Right
      const isRight = normalizedDiff === 1;
      return {
        transform: `translateX(${isRight ? '110%' : '-110%'}) scale(0.85)`,
        opacity: 0.6,
        zIndex: 20,
        filter: 'brightness(0.5)'
      };
    } else {
      // Far sides
      const isRight = normalizedDiff <= videos.length / 2;
      return {
        transform: `translateX(${isRight ? '200%' : '-200%'}) scale(0.7)`,
        opacity: 0,
        zIndex: 10,
        filter: 'brightness(0.3)'
      };
    }
  };

  return (
    <section id="videos" className="py-20 bg-[#050505] relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00D9FF] via-[#5B7FFF] to-[#FF1493] to-transparent" />
      
      {/* Background glow effects */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-[#00D9FF]/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-[#FF1493]/10 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-[#00D9FF]/20 to-[#FF1493]/20 border border-[#00D9FF]/30 text-[#00D9FF] mb-4">
            {language === 'fr' ? 'NOS INSPIRATIONS' : 'OUR INSPIRATIONS'}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {language === 'fr' ? 'Voyage' : 'Travel'}{' '}
            <span className="gradient-text">{language === 'fr' ? 'Visuel' : 'Visual'}</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {language === 'fr' 
              ? "Découvrez l'univers DNA Barbershop à travers nos vidéos exclusives"
              : "Discover the DNA Barbershop universe through our exclusive videos"
            }
          </p>
        </div>

        {/* Video Carousel */}
        <div className="relative max-w-6xl mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 md:-left-16 top-1/2 -translate-y-1/2 z-40 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 group"
          >
            <ChevronLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-0 md:-right-16 top-1/2 -translate-y-1/2 z-40 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 group"
          >
            <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Slider Container */}
          <div 
            ref={sliderRef}
            className="relative h-[500px] md:h-[600px] flex items-center justify-center"
          >
            {videos.map((video, index) => (
              <div
                key={video.id}
                className="absolute w-[280px] md:w-[340px] cursor-pointer transition-all duration-500 ease-out"
                style={getVideoStyle(index)}
                onClick={() => index === currentIndex && openModal(video)}
              >
                {/* Video Card */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                  {/* Video */}
                  <div className="aspect-[9/16] bg-black">
                    <video
                      src={video.url}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      onLoadedMetadata={(e) => {
                        if (video.startTime) {
                          e.target.currentTime = video.startTime;
                        }
                      }}
                    />
                  </div>
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  
                  {/* Play Button - Only on active */}
                  {index === currentIndex && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 transform group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-white ml-1" fill="white" />
                      </div>
                    </div>
                  )}
                  
                  {/* Title & Description */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white font-bold text-xl md:text-2xl mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {video.title}
                    </h3>
                    <p className="text-gray-300 text-sm md:text-base line-clamp-2">
                      {video.description[language]}
                    </p>
                  </div>

                  {/* Gradient border on active */}
                  {index === currentIndex && (
                    <div className="absolute inset-0 rounded-2xl border-2 border-[#00D9FF]/50 pointer-events-none" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-3 mt-8">
            {videos.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-gradient-to-r from-[#00D9FF] to-[#FF1493]' 
                    : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div 
            className="relative max-w-4xl w-full mx-4 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Video Container */}
            <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl shadow-[#00D9FF]/20">
              <video
                ref={modalVideoRef}
                src={selectedVideo.url}
                autoPlay
                loop
                playsInline
                className="w-full max-h-[80vh] object-contain"
              />
              
              {/* Mute Button */}
              <button
                onClick={toggleMute}
                className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>

            {/* Video Info */}
            <div className="mt-4 text-center">
              <h3 className="text-white font-bold text-2xl mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {selectedVideo.title}
              </h3>
              <p className="text-gray-400 text-lg">
                {selectedVideo.description[language]}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
